import { FormEvent, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMyProfile, formatSessionRemaining, logout } from '../../api/auth';
import { fetchInboxApprovalCount } from '../../api/approval';
import { createBoard, deleteBoard, fetchBoards, renameBoard } from '../../api/board';
import { IDLE_LOGOUT_MS, useIdleDeadline } from '../../hooks/useIdleDeadline';
import { useAuthStore } from '../../store/authStore';
import { useCompanyStore } from '../../store/companyStore';
import { Board } from '../../types/board';
import { isGuest } from '../../types/companyRole';
import { subscribeApprovalInboxChanged } from '../../utils/approvalInboxBadge';
import './Header.css';

import { reportError } from '../../utils/notify';
export const MENU_MY_PAGE = 'MyPage';
export const MENU_MY_PROFILE = '내정보 관리';
export const MENU_LEAVE = '휴가·출장';
export const MENU_GATE_LOG = '출입·수발신';
export const MENU_WORK_REPORT = '업무보고서';
export const MENU_INTERNAL_DRAFT = '내부 기안서';
export const MENU_MEETING_MINUTES = '회의록';
export const MENU_COMPANY = '회사관리';
export const MENU_ORG = '인사관리';
export const MENU_CONTRACT = '계약서';
export const MENU_OFFICIAL_DOCUMENT = '공문서';
export const MENU_BOARD = '게시판';
export const MENU_PRODUCT = '제품';
export const MENU_EQUIPMENT = '설비';
export const MENU_QUALITY = '품질';
export const MENU_SAFETY = '안전';
export const MENU_APPROVAL = '결재';

export const ORG_SUB_MEMBERS = '직원관리';
export const ORG_SUB_JOIN_REQUEST = '가입요청';
export const ORG_SUB_AUDIT_LOG = '감사 로그';

export const PRODUCT_SUB_LIST = '제품목록';
export const PRODUCT_SUB_INVENTORY = '재고관리';
export const PRODUCT_SUB_VENDOR = '업체관리';
export const PRODUCT_SUB_CERTIFICATE = '인증서';
export const PRODUCT_SUB_PROCESS_CHART = '공정도';
export const PRODUCT_SUB_PRODUCT_MANUAL = '사용설명서';
export const PRODUCT_SUB_DRAWING = '도면 관리';
export const PRODUCT_SUB_ORDER = '발주서';
export const PRODUCT_SUB_ACCEPTANCE = '인수검사';
export const PRODUCT_SUB_MID = '중간검사';
export const PRODUCT_SUB_FINAL = '제품검사';
export const PRODUCT_SUB_DAILY = '생산일보';
export const PRODUCT_SUB_NCR = '부적합품 보고서';

export const EQUIP_SUB_INSPECTION = '검사설비';
export const EQUIP_SUB_MANUFACTURING = '제조설비';

export const QUALITY_SUB_POLICY = '품질방침';
export const QUALITY_SUB_QC = 'QC 분석';
export const QUALITY_SUB_COMPLAINT_AS = '불만 및 AS 처리';
export const QUALITY_SUB_EDUCATION = '교육';
export const QUALITY_SUB_SMALL_GROUP = '소집단';
export const QUALITY_SUB_CLEAN_ACTIVITY = '청정활동';
export const QUALITY_SUB_SUGGESTION = '제안 신청서';
export const QUALITY_SUB_AUDIT = '내부심사';
export const QUALITY_SUB_EXTERNAL_AUDIT = '외부심사';

export const SAFETY_SUB_PPE = '안전장비 지급현황';
export const SAFETY_SUB_CHECK = '안전관리 점검시트';

export const ORG_SUB_MENUS = [ORG_SUB_MEMBERS, ORG_SUB_JOIN_REQUEST, ORG_SUB_AUDIT_LOG] as const;

export const PRODUCT_SUB_MENUS = [
  PRODUCT_SUB_LIST,
  PRODUCT_SUB_INVENTORY,
  PRODUCT_SUB_VENDOR,
  PRODUCT_SUB_CERTIFICATE,
  PRODUCT_SUB_PROCESS_CHART,
  PRODUCT_SUB_PRODUCT_MANUAL,
  PRODUCT_SUB_DRAWING,
  PRODUCT_SUB_ORDER,
  PRODUCT_SUB_ACCEPTANCE,
  PRODUCT_SUB_MID,
  PRODUCT_SUB_FINAL,
  PRODUCT_SUB_DAILY,
  PRODUCT_SUB_NCR,
] as const;

export const EQUIP_SUB_MENUS = [EQUIP_SUB_INSPECTION, EQUIP_SUB_MANUFACTURING] as const;

export const QUALITY_SUB_MENUS = [
  QUALITY_SUB_POLICY,
  QUALITY_SUB_QC,
  QUALITY_SUB_COMPLAINT_AS,
  QUALITY_SUB_EDUCATION,
  QUALITY_SUB_SMALL_GROUP,
  QUALITY_SUB_CLEAN_ACTIVITY,
  QUALITY_SUB_SUGGESTION,
  QUALITY_SUB_AUDIT,
  QUALITY_SUB_EXTERNAL_AUDIT,
] as const;

export const SAFETY_SUB_MENUS = [SAFETY_SUB_PPE, SAFETY_SUB_CHECK] as const;

export function isMyPageMenu(key: string) {
  return (
    key === MENU_MY_PAGE ||
    key === MENU_MY_PROFILE ||
    key === MENU_LEAVE ||
    key === MENU_GATE_LOG ||
    key === MENU_WORK_REPORT ||
    key === MENU_INTERNAL_DRAFT ||
    key === MENU_MEETING_MINUTES ||
    key === MENU_COMPANY
  );
}

export function isOrgMenu(key: string) {
  return key === MENU_ORG || (ORG_SUB_MENUS as readonly string[]).includes(key);
}

export function isProductMenu(key: string) {
  return key === MENU_PRODUCT || (PRODUCT_SUB_MENUS as readonly string[]).includes(key);
}

export function isEquipmentMenu(key: string) {
  return key === MENU_EQUIPMENT || (EQUIP_SUB_MENUS as readonly string[]).includes(key);
}

export function isQualityMenu(key: string) {
  return key === MENU_QUALITY || (QUALITY_SUB_MENUS as readonly string[]).includes(key);
}

export function isSafetyMenu(key: string) {
  return key === MENU_SAFETY || (SAFETY_SUB_MENUS as readonly string[]).includes(key);
}

interface HeaderProps {
  activeKey: string;
  onChangeActiveKey: (key: string, boardName?: string) => void;
}

function Header({ activeKey, onChangeActiveKey }: HeaderProps) {
  const navigate = useNavigate();
  const companyId = useCompanyStore((state) => state.companyId);
  const companyName = useCompanyStore((state) => state.companyName);
  const role = useCompanyStore((state) => state.role);
  const clearCompany = useCompanyStore((state) => state.clearCompany);
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [editingBoard, setEditingBoard] = useState<Board | null>(null);
  const [editBoardName, setEditBoardName] = useState('');
  const [boardMenu, setBoardMenu] = useState<{
    board: Board;
    x: number;
    y: number;
  } | null>(null);
  const [error, setError] = useState('');
  const [orgOpen, setOrgOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  const [qualityOpen, setQualityOpen] = useState(false);
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [myPageOpen, setMyPageOpen] = useState(false);
  const menuCloseTimerRef = useRef<number | null>(null);

  type MenuDropKey = 'myPage' | 'org' | 'product' | 'equipment' | 'quality' | 'safety';

  const clearMenuCloseTimer = () => {
    if (menuCloseTimerRef.current != null) {
      window.clearTimeout(menuCloseTimerRef.current);
      menuCloseTimerRef.current = null;
    }
  };

  const closeAllMenuDrops = () => {
    setMyPageOpen(false);
    setOrgOpen(false);
    setProductOpen(false);
    setEquipmentOpen(false);
    setQualityOpen(false);
    setSafetyOpen(false);
  };

  const openMenuDrop = (key: MenuDropKey) => {
    clearMenuCloseTimer();
    setMyPageOpen(key === 'myPage');
    setOrgOpen(key === 'org');
    setProductOpen(key === 'product');
    setEquipmentOpen(key === 'equipment');
    setQualityOpen(key === 'quality');
    setSafetyOpen(key === 'safety');
  };

  const scheduleCloseMenuDrop = () => {
    clearMenuCloseTimer();
    menuCloseTimerRef.current = window.setTimeout(() => {
      closeAllMenuDrops();
      menuCloseTimerRef.current = null;
    }, 180);
  };

  useEffect(() => () => clearMenuCloseTimer(), []);

  const [userName, setUserName] = useState('');
  const [sessionLabel, setSessionLabel] = useState('');
  const [sessionUrgent, setSessionUrgent] = useState(false);
  const [approvalInboxCount, setApprovalInboxCount] = useState(0);
  const sessionExpiresAt = useAuthStore((s) => s.sessionExpiresAt);
  const rememberMe = useAuthStore((s) => s.rememberMe);
  const loggingOutRef = useRef(false);
  const isOwner = role === 'OWNER';
  const guestMode = isGuest(role);

  const expireIdleSession = useCallback(() => {
    if (loggingOutRef.current || rememberMe) return;
    loggingOutRef.current = true;
    setSessionLabel('세션 만료');
    setSessionUrgent(true);
    window.alert('60분 동안 사용이 없어 로그아웃되었습니다. 다시 로그인해 주세요.');
    void logout().then(() => navigate('/login', { replace: true }));
  }, [rememberMe, navigate]);

  const { getDeadline: getIdleDeadline } = useIdleDeadline(
    !rememberMe && !guestMode,
    IDLE_LOGOUT_MS,
    expireIdleSession
  );
  const plan = useCompanyStore((state) => state.plan);
  const isPro = (plan || '').toUpperCase() === 'PRO';
  const myPageActive = isMyPageMenu(activeKey);
  const orgMenus = ORG_SUB_MENUS.filter(
    (item) => item !== ORG_SUB_AUDIT_LOG || (isOwner && isPro)
  );

  const refreshApprovalBadge = useCallback(async () => {
    if (!companyId) {
      setApprovalInboxCount(0);
      return;
    }
    try {
      const count = await fetchInboxApprovalCount();
      setApprovalInboxCount(count > 0 ? count : 0);
    } catch {
      // 뱃지 실패는 무시 (메뉴 자체는 유지)
    }
  }, [companyId]);

  useEffect(() => {
    if (guestMode) {
      setApprovalInboxCount(0);
      return;
    }
    void refreshApprovalBadge();
    const intervalId = window.setInterval(() => {
      void refreshApprovalBadge();
    }, 30000);
    const onFocus = () => void refreshApprovalBadge();
    window.addEventListener('focus', onFocus);
    const unsubscribe = subscribeApprovalInboxChanged((detail) => {
      if (typeof detail?.count === 'number' && Number.isFinite(detail.count)) {
        setApprovalInboxCount(detail.count > 0 ? detail.count : 0);
        return;
      }
      void refreshApprovalBadge();
    });
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      unsubscribe();
    };
  }, [refreshApprovalBadge, guestMode]);

  useEffect(() => {
    // 로그인 유지: 서버 만료시각(최대 7일). 일반: 사용자 유휴 60분(클릭·키입력 등만 리셋)
    if (rememberMe) {
      if (!sessionExpiresAt) {
        setSessionLabel('');
        setSessionUrgent(false);
        return;
      }
      let cancelled = false;
      const tick = () => {
        if (cancelled) return;
        const remain = sessionExpiresAt - Date.now();
        if (remain <= 0) {
          setSessionLabel('세션 만료');
          setSessionUrgent(true);
          window.alert('세션이 만료되어 로그아웃되었습니다. 다시 로그인해 주세요.');
          void logout().then(() => navigate('/login', { replace: true }));
          return;
        }
        setSessionLabel(`로그인 유지 · ${formatSessionRemaining(remain)}`);
        setSessionUrgent(false);
      };
      tick();
      const id = window.setInterval(tick, 1000);
      return () => {
        cancelled = true;
        window.clearInterval(id);
      };
    }

    if (guestMode) {
      setSessionLabel('');
      setSessionUrgent(false);
      return;
    }

    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const deadline = getIdleDeadline();
      if (deadline == null) {
        setSessionLabel('');
        setSessionUrgent(false);
        return;
      }
      const remain = deadline - Date.now();
      if (remain <= 0) return; // logout은 useIdleDeadline이 처리
      setSessionLabel(`자동 로그아웃 ${formatSessionRemaining(remain)}`);
      setSessionUrgent(remain <= 5 * 60 * 1000);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [sessionExpiresAt, rememberMe, guestMode, navigate, getIdleDeadline]);

  const loadBoards = async () => {
    try {
      setLoading(true);
      const list = await fetchBoards();
      setBoards(Array.isArray(list) ? list : []);
      setError('');
    } catch {
      reportError(setError, '회사 공통 메뉴를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBoards();
  }, [companyId]);

  useEffect(() => {
    const load = async () => {
      try {
        const profile = await fetchMyProfile();
        setUserName((profile.nickname || '').trim());
      } catch {
        setUserName('');
      }
    };
    void load();
    const onName = (event: Event) => {
      const name = (event as CustomEvent<string>).detail;
      if (name) setUserName(name);
    };
    window.addEventListener('linelog:profile-name', onName);
    return () => window.removeEventListener('linelog:profile-name', onName);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleSwitchCompany = () => {
    clearCompany();
    navigate('/companies', { replace: true });
  };

  const handleAddBoard = async (event: FormEvent) => {
    event.preventDefault();
    if (!isOwner) {
      reportError(setError, '회사 공통 메뉴는 소유자만 추가할 수 있습니다.');
      return;
    }

    const name = newBoardName.trim();
    if (!name) {
      reportError(setError, '메뉴명을 입력해 주세요.');
      return;
    }

    try {
      setAdding(true);
      const created = await createBoard(name);
      setBoards((prev) => [...prev, created]);
      onChangeActiveKey(String(created.boardId), created.boardName);
      setNewBoardName('');
      setShowAddForm(false);
      setError('');
    } catch {
      reportError(setError, '회사 공통 메뉴 추가에 실패했습니다.');
    } finally {
      setAdding(false);
    }
  };

  const openBoardContextMenu = (event: MouseEvent, board: Board) => {
    if (!isOwner || guestMode) return;
    event.preventDefault();
    event.stopPropagation();
    setBoardMenu({ board, x: event.clientX, y: event.clientY });
    setShowAddForm(false);
    setEditingBoard(null);
    setError('');
  };

  const startRenameBoard = (board: Board) => {
    setBoardMenu(null);
    setShowAddForm(false);
    setEditingBoard(board);
    setEditBoardName(board.boardName);
    setError('');
  };

  const handleRenameBoard = async (event: FormEvent) => {
    event.preventDefault();
    if (!editingBoard || !isOwner) return;
    const name = editBoardName.trim();
    if (!name) {
      reportError(setError, '메뉴명을 입력해 주세요.');
      return;
    }
    try {
      setAdding(true);
      const updated = await renameBoard(editingBoard.boardId, name);
      setBoards((prev) =>
        prev.map((item) => (item.boardId === updated.boardId ? updated : item))
      );
      if (activeKey === String(updated.boardId)) {
        onChangeActiveKey(String(updated.boardId), updated.boardName);
      }
      setEditingBoard(null);
      setEditBoardName('');
      setError('');
    } catch {
      reportError(setError, '메뉴 이름 변경에 실패했습니다.');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteBoard = async (board: Board) => {
    setBoardMenu(null);
    if (!isOwner) return;
    const ok = window.confirm(
      `'${board.boardName}' 메뉴를 삭제할까요?\n안의 폴더·파일도 모두 삭제됩니다.`
    );
    if (!ok) return;
    try {
      setAdding(true);
      await deleteBoard(board.boardId);
      setBoards((prev) => prev.filter((item) => item.boardId !== board.boardId));
      if (activeKey === String(board.boardId)) {
        onChangeActiveKey(MENU_MY_PAGE);
      }
      if (editingBoard?.boardId === board.boardId) {
        setEditingBoard(null);
        setEditBoardName('');
      }
      setError('');
    } catch {
      reportError(setError, '메뉴 삭제에 실패했습니다.');
    } finally {
      setAdding(false);
    }
  };

  useEffect(() => {
    if (!boardMenu) return;
    const close = () => setBoardMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [boardMenu]);

  return (
    <header className="ec-header">
      <div className="ec-header__main">
        <div className="ec-header__brand">
          <button
            type="button"
            className="ec-header__logo"
            onClick={() => navigate('/')}
            title="Linelog 홈"
          >
            <em>Line</em>log
          </button>
          {companyName && (
            <button
              type="button"
              className="ec-header__company"
              onClick={handleSwitchCompany}
              title="회사 변경"
            >
              {companyName}
            </button>
          )}
        </div>

        <nav className="ec-header__menus" aria-label="메인 메뉴">
          <div
            className={`ec-header__menu-drop${myPageActive ? ' is-active' : ''}${
              myPageOpen ? ' is-open' : ''
            }`}
            onMouseEnter={() => openMenuDrop('myPage')}
            onMouseLeave={scheduleCloseMenuDrop}
          >
            <button
              type="button"
              className={`ec-header__menu${myPageActive ? ' is-active' : ''}`}
              onClick={() => {
                openMenuDrop('myPage');
                onChangeActiveKey(MENU_MY_PAGE);
              }}
              aria-haspopup="true"
              aria-expanded={myPageOpen}
            >
              {MENU_MY_PAGE}
            </button>
            <div className="ec-header__dropdown" role="menu">
              <button
                type="button"
                role="menuitem"
                className={`ec-header__dropdown-item${
                  activeKey === MENU_MY_PROFILE ? ' is-active' : ''
                }`}
                onClick={() => {
                  onChangeActiveKey(MENU_MY_PROFILE);
                  closeAllMenuDrops();
                }}
              >
                {MENU_MY_PROFILE}
              </button>
              {!guestMode && (
                <>
              <button
                type="button"
                role="menuitem"
                className={`ec-header__dropdown-item${
                  activeKey === MENU_LEAVE ? ' is-active' : ''
                }`}
                onClick={() => {
                  onChangeActiveKey(MENU_LEAVE);
                  closeAllMenuDrops();
                }}
              >
                {MENU_LEAVE}
              </button>
              <button
                type="button"
                role="menuitem"
                className={`ec-header__dropdown-item${
                  activeKey === MENU_GATE_LOG ? ' is-active' : ''
                }`}
                onClick={() => {
                  onChangeActiveKey(MENU_GATE_LOG);
                  closeAllMenuDrops();
                }}
              >
                {MENU_GATE_LOG}
              </button>
              <button
                type="button"
                role="menuitem"
                className={`ec-header__dropdown-item${
                  activeKey === MENU_WORK_REPORT ? ' is-active' : ''
                }`}
                onClick={() => {
                  onChangeActiveKey(MENU_WORK_REPORT);
                  closeAllMenuDrops();
                }}
              >
                {MENU_WORK_REPORT}
              </button>
                </>
              )}
              <button
                type="button"
                role="menuitem"
                className={`ec-header__dropdown-item${
                  activeKey === MENU_INTERNAL_DRAFT ? ' is-active' : ''
                }`}
                onClick={() => {
                  onChangeActiveKey(MENU_INTERNAL_DRAFT);
                  closeAllMenuDrops();
                }}
              >
                {MENU_INTERNAL_DRAFT}
              </button>
              <button
                type="button"
                role="menuitem"
                className={`ec-header__dropdown-item${
                  activeKey === MENU_MEETING_MINUTES ? ' is-active' : ''
                }`}
                onClick={() => {
                  onChangeActiveKey(MENU_MEETING_MINUTES);
                  closeAllMenuDrops();
                }}
              >
                {MENU_MEETING_MINUTES}
              </button>
              {!guestMode && isOwner && (
                <button
                  type="button"
                  role="menuitem"
                  className={`ec-header__dropdown-item${
                    activeKey === MENU_COMPANY ? ' is-active' : ''
                  }`}
                  onClick={() => {
                    onChangeActiveKey(MENU_COMPANY);
                    closeAllMenuDrops();
                  }}
                >
                  회사 관리
                </button>
              )}
            </div>
          </div>

          {!guestMode && isOwner && (
            <div
              className={`ec-header__menu-drop${isOrgMenu(activeKey) ? ' is-active' : ''}${
                orgOpen ? ' is-open' : ''
              }`}
              onMouseEnter={() => openMenuDrop('org')}
              onMouseLeave={scheduleCloseMenuDrop}
            >
              <button
                type="button"
                className={`ec-header__menu${isOrgMenu(activeKey) ? ' is-active' : ''}`}
                onClick={() => {
                  openMenuDrop('org');
                  onChangeActiveKey(ORG_SUB_MEMBERS);
                }}
                aria-haspopup="true"
                aria-expanded={orgOpen}
              >
                {MENU_ORG}
              </button>
              <div className="ec-header__dropdown" role="menu">
                {orgMenus.map((item) => (
                  <button
                    key={item}
                    type="button"
                    role="menuitem"
                    className={`ec-header__dropdown-item${
                      activeKey === item || (item === ORG_SUB_MEMBERS && activeKey === MENU_ORG)
                        ? ' is-active'
                        : ''
                    }`}
                    onClick={() => {
                      onChangeActiveKey(item);
                      closeAllMenuDrops();
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            className={`ec-header__menu${activeKey === MENU_CONTRACT ? ' is-active' : ''}`}
            onClick={() => onChangeActiveKey(MENU_CONTRACT)}
          >
            {MENU_CONTRACT}
          </button>

          <button
            type="button"
            className={`ec-header__menu${activeKey === MENU_OFFICIAL_DOCUMENT ? ' is-active' : ''}`}
            onClick={() => onChangeActiveKey(MENU_OFFICIAL_DOCUMENT)}
          >
            {MENU_OFFICIAL_DOCUMENT}
          </button>

          <button
            type="button"
            className={`ec-header__menu${activeKey === MENU_BOARD ? ' is-active' : ''}`}
            onClick={() => onChangeActiveKey(MENU_BOARD)}
          >
            {MENU_BOARD}
          </button>

          <div
            className={`ec-header__menu-drop${isProductMenu(activeKey) ? ' is-active' : ''}${
              productOpen ? ' is-open' : ''
            }`}
            onMouseEnter={() => openMenuDrop('product')}
            onMouseLeave={scheduleCloseMenuDrop}
          >
            <button
              type="button"
              className={`ec-header__menu${isProductMenu(activeKey) ? ' is-active' : ''}`}
              onClick={() => {
                openMenuDrop('product');
                onChangeActiveKey(PRODUCT_SUB_LIST);
              }}
              aria-haspopup="true"
              aria-expanded={productOpen}
            >
              {MENU_PRODUCT}
            </button>
            <div className="ec-header__dropdown" role="menu">
              {PRODUCT_SUB_MENUS.map((item) => (
                <button
                  key={item}
                  type="button"
                  role="menuitem"
                  className={`ec-header__dropdown-item${
                    activeKey === item ? ' is-active' : ''
                  }`}
                  onClick={() => {
                    onChangeActiveKey(item);
                    closeAllMenuDrops();
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div
            className={`ec-header__menu-drop${isEquipmentMenu(activeKey) ? ' is-active' : ''}${
              equipmentOpen ? ' is-open' : ''
            }`}
            onMouseEnter={() => openMenuDrop('equipment')}
            onMouseLeave={scheduleCloseMenuDrop}
          >
            <button
              type="button"
              className={`ec-header__menu${isEquipmentMenu(activeKey) ? ' is-active' : ''}`}
              onClick={() => {
                openMenuDrop('equipment');
                onChangeActiveKey(EQUIP_SUB_INSPECTION);
              }}
              aria-haspopup="true"
              aria-expanded={equipmentOpen}
            >
              {MENU_EQUIPMENT}
            </button>
            <div className="ec-header__dropdown" role="menu">
              {EQUIP_SUB_MENUS.map((item) => (
                <button
                  key={item}
                  type="button"
                  role="menuitem"
                  className={`ec-header__dropdown-item${
                    activeKey === item ? ' is-active' : ''
                  }`}
                  onClick={() => {
                    onChangeActiveKey(item);
                    closeAllMenuDrops();
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div
            className={`ec-header__menu-drop${isQualityMenu(activeKey) ? ' is-active' : ''}${
              qualityOpen ? ' is-open' : ''
            }`}
            onMouseEnter={() => openMenuDrop('quality')}
            onMouseLeave={scheduleCloseMenuDrop}
          >
            <button
              type="button"
              className={`ec-header__menu${isQualityMenu(activeKey) ? ' is-active' : ''}`}
              onClick={() => {
                openMenuDrop('quality');
                onChangeActiveKey(QUALITY_SUB_POLICY);
              }}
              aria-haspopup="true"
              aria-expanded={qualityOpen}
            >
              {MENU_QUALITY}
            </button>
            <div className="ec-header__dropdown" role="menu">
              {QUALITY_SUB_MENUS.filter((item) => !(guestMode && item === QUALITY_SUB_QC)).map(
                (item) => (
                <button
                  key={item}
                  type="button"
                  role="menuitem"
                  className={`ec-header__dropdown-item${
                    activeKey === item ? ' is-active' : ''
                  }`}
                  onClick={() => {
                    onChangeActiveKey(item);
                    closeAllMenuDrops();
                  }}
                >
                  {item}
                </button>
              )
              )}
            </div>
          </div>

          <div
            className={`ec-header__menu-drop${isSafetyMenu(activeKey) ? ' is-active' : ''}${
              safetyOpen ? ' is-open' : ''
            }`}
            onMouseEnter={() => openMenuDrop('safety')}
            onMouseLeave={scheduleCloseMenuDrop}
          >
            <button
              type="button"
              className={`ec-header__menu${isSafetyMenu(activeKey) ? ' is-active' : ''}`}
              onClick={() => {
                openMenuDrop('safety');
                onChangeActiveKey(SAFETY_SUB_PPE);
              }}
              aria-haspopup="true"
              aria-expanded={safetyOpen}
            >
              {MENU_SAFETY}
            </button>
            <div className="ec-header__dropdown" role="menu">
              {SAFETY_SUB_MENUS.map((item) => (
                <button
                  key={item}
                  type="button"
                  role="menuitem"
                  className={`ec-header__dropdown-item${
                    activeKey === item ? ' is-active' : ''
                  }`}
                  onClick={() => {
                    onChangeActiveKey(item);
                    closeAllMenuDrops();
                  }}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className="ec-header__boards">
            {boards.map((board) => (
              <button
                key={board.boardId}
                type="button"
                className={`ec-header__menu ec-header__board-tab${
                  activeKey === String(board.boardId) ? ' is-active' : ''
                }`}
                onClick={() => onChangeActiveKey(String(board.boardId), board.boardName)}
                onContextMenu={(e) => openBoardContextMenu(e, board)}
                title={
                  isOwner && !guestMode
                    ? `${board.boardName} (우클릭: 수정/삭제)`
                    : board.boardName
                }
              >
                {board.boardName}
              </button>
            ))}

            {!guestMode && isOwner && (
              <button
                type="button"
                className="ec-header__add"
                title="회사 공통 메뉴 추가"
                aria-label="회사 공통 메뉴 추가"
                onClick={() => {
                  setShowAddForm((prev) => !prev);
                  setEditingBoard(null);
                  setBoardMenu(null);
                  setError('');
                }}
              >
                +
              </button>
            )}

            {loading && <span className="ec-header__hint">불러오는 중...</span>}
          </div>
        </nav>

        <div className="ec-header__utils">
          {!guestMode ? (
          <button
            type="button"
            className={`ec-header__menu ec-header__approval${
              activeKey === MENU_APPROVAL ? ' is-active' : ''
            }`}
            onClick={() => onChangeActiveKey(MENU_APPROVAL)}
            aria-label={
              approvalInboxCount > 0
                ? `결재, 대기 ${approvalInboxCount}건`
                : '결재'
            }
          >
            {MENU_APPROVAL}
            {approvalInboxCount > 0 ? (
              <span className="ec-header__approval-badge">
                {approvalInboxCount > 99 ? '99+' : approvalInboxCount}
              </span>
            ) : null}
          </button>
          ) : (
            <span className="ec-header__guest-badge" title="읽기 전용">
              게스트 · 읽기 전용
            </span>
          )}
          <div className="ec-header__user">
            <span className="ec-header__greeting">
              {userName ? `${userName} 님` : '환영합니다'}
            </span>
            {sessionLabel ? (
              <span
                className={`ec-header__session${sessionUrgent ? ' is-urgent' : ''}`}
                title={rememberMe ? '로그인 유지 · 최대 7일' : '미사용 60분 후 자동 로그아웃'}
              >
                {sessionLabel}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            className="ec-logout-btn"
            onClick={handleLogout}
            title="로그아웃"
          >
            로그아웃
          </button>
        </div>
      </div>

      {showAddForm && isOwner && (
        <div className="ec-header__add-bar">
          <form className="ec-header__add-form" onSubmit={handleAddBoard}>
            <input
              type="text"
              className="ec-header__add-input"
              value={newBoardName}
              onChange={(e) => setNewBoardName(e.target.value)}
              placeholder="회사 공통 메뉴명 입력"
              maxLength={50}
              autoFocus
            />
            <button type="submit" className="ec-header__add-submit" disabled={adding}>
              {adding ? '추가 중...' : '추가'}
            </button>
            <button
              type="button"
              className="ec-header__add-cancel"
              onClick={() => {
                setShowAddForm(false);
                setNewBoardName('');
                setError('');
              }}
            >
              취소
            </button>
          </form>
          {error && <p className="ec-header__error">{error}</p>}
        </div>
      )}

      {editingBoard && isOwner && (
        <div className="ec-header__add-bar">
          <form className="ec-header__add-form" onSubmit={(e) => void handleRenameBoard(e)}>
            <input
              type="text"
              className="ec-header__add-input"
              value={editBoardName}
              onChange={(e) => setEditBoardName(e.target.value)}
              placeholder="메뉴명 수정"
              maxLength={50}
              autoFocus
            />
            <button type="submit" className="ec-header__add-submit" disabled={adding}>
              {adding ? '저장 중...' : '저장'}
            </button>
            <button
              type="button"
              className="ec-header__add-cancel"
              onClick={() => {
                setEditingBoard(null);
                setEditBoardName('');
                setError('');
              }}
            >
              취소
            </button>
          </form>
          {error && <p className="ec-header__error">{error}</p>}
        </div>
      )}

      {boardMenu && isOwner ? (
        <div
          className="ec-header__board-menu"
          style={{ top: boardMenu.y, left: boardMenu.x }}
          role="menu"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => startRenameBoard(boardMenu.board)}
          >
            이름 변경
          </button>
          <button
            type="button"
            role="menuitem"
            className="is-danger"
            onClick={() => void handleDeleteBoard(boardMenu.board)}
          >
            삭제
          </button>
        </div>
      ) : null}

      {!showAddForm && !editingBoard && error && (
        <div className="ec-header__add-bar">
          <p className="ec-header__error">{error}</p>
        </div>
      )}
    </header>
  );
}

export default Header;
